import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Compass,
  Library,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  TriangleAlert,
  Unplug,
  Volume2,
} from 'lucide-react';

import { AppDialog } from 'components/ui/app-dialog';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { useToast } from 'components/ui/use-toast';
import {
  adminContent,
  type ElevenLabsRoleplayVoice,
  type ElevenLabsRoleplayVoiceLibrary,
  type ElevenLabsRoleplayVoiceMapping,
  type ElevenLabsRoleplayVoiceUsage,
  type ElevenLabsSharedVoice,
  type ElevenLabsSharedVoiceSearchResult,
} from 'services/api/adminContentClient';

let unresolvedLibraryRequest: Promise<ElevenLabsRoleplayVoiceLibrary> | null = null;

const getVoiceLibraryOnce = () => {
  if (unresolvedLibraryRequest) return unresolvedLibraryRequest;
  const request = adminContent.operations.getElevenLabsRoleplayVoices()
    .then(({ data }) => data);
  unresolvedLibraryRequest = request;
  const clear = () => {
    if (unresolvedLibraryRequest === request) unresolvedLibraryRequest = null;
  };
  request.then(clear, clear);
  return request;
};

const apiErrorMessage = (error: unknown, fallback: string) => {
  const detail = (error as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail;
  return typeof detail === 'string' && detail.trim() ? detail : fallback;
};

const voiceSearchText = (voice: ElevenLabsRoleplayVoice) => [
  voice.name,
  voice.voice_id,
  voice.category,
  voice.description,
  ...Object.values(voice.labels),
].join(' ').toLocaleLowerCase();

const usageSummary = (mapping: ElevenLabsRoleplayVoiceMapping) => {
  const parts = [];
  if (mapping.usage.question_count) {
    parts.push(
      `${mapping.usage.question_count} question${mapping.usage.question_count === 1 ? '' : 's'}`,
    );
  }
  if (mapping.usage.active_session_count) {
    parts.push(
      `${mapping.usage.active_session_count} open attempt${mapping.usage.active_session_count === 1 ? '' : 's'}`,
    );
  }
  return parts.length ? parts.join(' · ') : 'Not used by roleplay content';
};

const questionEditHref = (questionId: number) => (
  `/admin/content?tab=question-library&qlQuestionId=${encodeURIComponent(questionId)}`
);

const removalConflictUsage = (error: unknown): ElevenLabsRoleplayVoiceUsage | null => {
  const response = (error as {
    response?: {
      status?: number;
      data?: { usage?: ElevenLabsRoleplayVoiceUsage };
    };
  })?.response;
  return response?.status === 409 ? response.data?.usage ?? null : null;
};

const sharedVoiceLabels = (voice: ElevenLabsSharedVoice) => Array.from(new Set([
  voice.language,
  voice.accent,
  voice.gender,
  voice.age,
  voice.descriptive,
  voice.use_case.replaceAll('_', ' '),
].filter(Boolean)));

type VoiceLibraryTab = 'saved' | 'explore';

const ElevenLabsRoleplayVoicesCard: React.FC = () => {
  const { addToast } = useToast();
  const [library, setLibrary] = useState<ElevenLabsRoleplayVoiceLibrary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [actionInFlight, setActionInFlight] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [newVoiceId, setNewVoiceId] = useState('');
  const [editingAlias, setEditingAlias] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editVoiceId, setEditVoiceId] = useState('');
  const [pendingRemoval, setPendingRemoval] = useState<ElevenLabsRoleplayVoiceMapping | null>(null);
  const [removalConflict, setRemovalConflict] = useState<ElevenLabsRoleplayVoiceMapping | null>(null);
  const [isVoiceLibraryOpen, setIsVoiceLibraryOpen] = useState(false);
  const [voiceLibraryTab, setVoiceLibraryTab] = useState<VoiceLibraryTab>('saved');
  const [sharedSearch, setSharedSearch] = useState('');
  const [submittedSharedSearch, setSubmittedSharedSearch] = useState('');
  const [sharedResults, setSharedResults] = useState<ElevenLabsSharedVoiceSearchResult | null>(null);
  const [isSearchingShared, setIsSearchingShared] = useState(false);
  const [pendingSharedVoice, setPendingSharedVoice] = useState<ElevenLabsSharedVoice | null>(null);
  const [sharedSavedName, setSharedSavedName] = useState('');
  const [recentlySavedSharedVoiceIds, setRecentlySavedSharedVoiceIds] = useState<Set<string>>(
    () => new Set(),
  );

  const loadLibrary = useCallback(async ({ refresh = false }: { refresh?: boolean } = {}) => {
    if (refresh) setIsRefreshing(true);
    else setIsLoading(true);
    try {
      const data = refresh
        ? (await adminContent.operations.getElevenLabsRoleplayVoices()).data
        : await getVoiceLibraryOnce();
      setLibrary(data);
    } catch (error) {
      addToast({
        title: 'Failed to load ElevenLabs voices',
        description: apiErrorMessage(error, 'Please refresh and try again.'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [addToast]);

  useEffect(() => {
    let isMounted = true;
    void (async () => {
      try {
        const data = await getVoiceLibraryOnce();
        if (isMounted) setLibrary(data);
      } catch (error) {
        if (isMounted) {
          addToast({
            title: 'Failed to load ElevenLabs voices',
            description: apiErrorMessage(error, 'Please refresh and try again.'),
            variant: 'destructive',
          });
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [addToast]);

  const filteredVoices = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    if (!query) return library?.voices ?? [];
    return (library?.voices ?? []).filter((voice) => voiceSearchText(voice).includes(query));
  }, [library?.voices, search]);

  // The WiseFriend name is generated on the backend from the ElevenLabs voice
  // name, so adding a voice is a single step.
  const addVoice = async (voiceId: string) => {
    if (!voiceId) return;
    setActionInFlight(`create:${voiceId}`);
    try {
      const response = await adminContent.operations.createElevenLabsRoleplayVoice(voiceId);
      setLibrary(response.data);
      if (newVoiceId === voiceId) setNewVoiceId('');
      const createdName = response.data.mappings
        .find((mapping) => mapping.voice.voice_id === voiceId)?.name
        ?? library?.voices.find((voice) => voice.voice_id === voiceId)?.name
        ?? 'Voice';
      addToast({
        title: `${createdName} added to WiseFriend`,
        description: 'The voice is now available in roleplay question authoring. Rename it here if you want a friendlier label.',
      });
    } catch (error) {
      addToast({
        title: 'Could not add voice',
        description: apiErrorMessage(error, 'Please check the voice and try again.'),
        variant: 'destructive',
      });
    } finally {
      setActionInFlight(null);
    }
  };

  const beginEdit = (mapping: ElevenLabsRoleplayVoiceMapping) => {
    setEditingAlias(mapping.alias);
    setEditName(mapping.name);
    setEditVoiceId(mapping.voice.voice_id);
  };

  const saveEdit = async (mapping: ElevenLabsRoleplayVoiceMapping) => {
    const normalizedName = editName.trim();
    if (!normalizedName || !editVoiceId) return;
    setActionInFlight(`edit:${mapping.alias}`);
    try {
      const response = await adminContent.operations.updateElevenLabsRoleplayVoice(
        mapping.alias,
        normalizedName,
        editVoiceId,
      );
      setLibrary(response.data);
      setEditingAlias(null);
      addToast({
        title: `${normalizedName} updated`,
        description: 'Roleplay questions keep the same stable internal voice reference.',
      });
    } catch (error) {
      addToast({
        title: 'Could not update voice name',
        description: apiErrorMessage(error, 'Please refresh and try again.'),
        variant: 'destructive',
      });
    } finally {
      setActionInFlight(null);
    }
  };

  const confirmRemoval = async () => {
    if (!pendingRemoval) return;
    const removedName = pendingRemoval.name;
    setActionInFlight(`remove:${pendingRemoval.alias}`);
    try {
      const response = await adminContent.operations.removeElevenLabsRoleplayVoice(
        pendingRemoval.alias,
      );
      setLibrary(response.data);
      setPendingRemoval(null);
      addToast({
        title: `${removedName} removed from WiseFriend`,
        description: 'The voice remains untouched in your ElevenLabs account.',
      });
    } catch (error) {
      const usage = removalConflictUsage(error);
      if (usage) {
        const conflictedMapping = {
          ...pendingRemoval,
          usage,
          can_remove: false,
        };
        setLibrary((current) => current ? {
          ...current,
          mappings: current.mappings.map((mapping) => (
            mapping.alias === conflictedMapping.alias ? conflictedMapping : mapping
          )),
        } : current);
        setPendingRemoval(null);
        setRemovalConflict(conflictedMapping);
        return;
      }
      addToast({
        title: 'Could not remove voice name',
        description: apiErrorMessage(
          error,
          'Remove its roleplay question usage and try again.',
        ),
        variant: 'destructive',
      });
    } finally {
      setActionInFlight(null);
    }
  };

  const requestRemoval = (mapping: ElevenLabsRoleplayVoiceMapping) => {
    if (!mapping.can_remove) {
      setRemovalConflict(mapping);
      return;
    }
    setPendingRemoval(mapping);
  };

  const searchSharedVoices = async (page = 0) => {
    setIsSearchingShared(true);
    const query = page === 0 ? sharedSearch.trim() : submittedSharedSearch;
    try {
      const response = await adminContent.operations.searchElevenLabsSharedVoices(
        query,
        page,
      );
      setSharedResults(response.data);
      setSubmittedSharedSearch(query);
    } catch (error) {
      addToast({
        title: 'Could not search ElevenLabs voices',
        description: apiErrorMessage(error, 'Please check the provider connection and try again.'),
        variant: 'destructive',
      });
    } finally {
      setIsSearchingShared(false);
    }
  };

  const beginSaveSharedVoice = (voice: ElevenLabsSharedVoice) => {
    setIsVoiceLibraryOpen(false);
    setPendingSharedVoice(voice);
    setSharedSavedName(voice.name);
  };

  const confirmSaveSharedVoice = async () => {
    if (!pendingSharedVoice || !sharedSavedName.trim()) return;
    const sourceVoiceId = pendingSharedVoice.voice_id;
    const savedName = sharedSavedName.trim();
    setActionInFlight(`save-shared:${sourceVoiceId}`);
    try {
      const response = await adminContent.operations.addElevenLabsSharedVoice(
        pendingSharedVoice.public_owner_id,
        sourceVoiceId,
        savedName,
      );
      setLibrary(response.data);
      setRecentlySavedSharedVoiceIds((current) => new Set(current).add(sourceVoiceId));
      setPendingSharedVoice(null);
      setSharedSavedName('');
      setVoiceLibraryTab('saved');
      addToast({
        title: `${savedName} saved in ElevenLabs`,
        description: 'It is now a saved voice you can add to WiseFriend.',
      });
    } catch (error) {
      addToast({
        title: 'Could not save ElevenLabs voice',
        description: apiErrorMessage(error, 'Please verify your ElevenLabs access and try again.'),
        variant: 'destructive',
      });
    } finally {
      setActionInFlight(null);
    }
  };

  const voiceOptions = (
    currentVoice?: ElevenLabsRoleplayVoice,
    currentAlias?: string,
  ) => {
    const voices = library?.voices ?? [];
    const hasCurrent = currentVoice
      ? voices.some((voice) => voice.voice_id === currentVoice.voice_id)
      : true;
    return (
      <>
        {!hasCurrent && currentVoice ? (
          <SelectItem value={currentVoice.voice_id}>
            {currentVoice.name} · currently unavailable
          </SelectItem>
        ) : null}
        {voices.map((voice) => {
          const mappedElsewhere = (voice.mapped_aliases ?? []).some(
            (alias) => alias !== currentAlias,
          );
          return (
            <SelectItem
              key={voice.voice_id}
              value={voice.voice_id}
              disabled={!voice.is_model_compatible || mappedElsewhere}
            >
              {voice.name}
              {!voice.is_model_compatible ? ' · incompatible' : ''}
              {mappedElsewhere ? ' · already named' : ''}
            </SelectItem>
          );
        })}
      </>
    );
  };

  return (
    <Card
      className="w-full max-w-none overflow-hidden"
      maxWidth="none"
      data-testid="elevenlabs-roleplay-voices"
    >
      <CardHeader className="gap-3 sm:flex-row sm:items-start sm:justify-between sm:space-y-0">
        <CardTitle>
          <span className="flex items-center gap-2">
            <Volume2 className="h-5 w-5 text-foundation-orange-500" aria-hidden="true" />
            ElevenLabs Roleplay Voices
          </span>
        </CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          {isLoading && !library ? (
            <Badge variant="outline" className="gap-1.5">
              <RefreshCw className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              Loading saved voices
            </Badge>
          ) : library?.provider_connected ? (
            <Badge variant="secondary" className="gap-1.5 border border-emerald-200 bg-emerald-50 text-emerald-800">
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
              Connected · {library.voices.length} saved
            </Badge>
          ) : (
            <Badge variant="outline" className="gap-1.5 border-amber-300 bg-amber-50 text-amber-900">
              <Unplug className="h-3.5 w-3.5" aria-hidden="true" />
              {library?.provider_configured ? 'Connection needs attention' : 'API key required'}
            </Badge>
          )}
          <Button
            type="button"
            size="auto"
            variant="secondary"
            disabled={isLoading || !library}
            onClick={() => setIsVoiceLibraryOpen(true)}
          >
            <Library aria-hidden="true" />
            Browse voice library
          </Button>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            aria-label="Refresh ElevenLabs voice library"
            title="Refresh ElevenLabs voice library"
            disabled={isLoading || isRefreshing || actionInFlight !== null}
            onClick={() => { void loadLibrary({ refresh: true }); }}
          >
            <RefreshCw className={isRefreshing ? 'animate-spin' : ''} aria-hidden="true" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="items-stretch gap-6">
        <div className="max-w-4xl space-y-1 text-sm text-muted-foreground">
          <p>
            Add any saved ElevenLabs voice to WiseFriend. It appears under its ElevenLabs name beside the built-in OpenAI voices when staff author a roleplay question.
          </p>
          <p className="text-xs">
            WiseFriend never edits or deletes provider voices. Browse the voice library to preview saved voices or discover another voice from ElevenLabs.
          </p>
        </div>

        {isLoading ? (
          <div className="h-48 animate-pulse rounded-2xl border border-border/60 bg-white/40" />
        ) : library ? (
          <>
            {library.provider_error ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950" role="status">
                <p className="font-semibold">Voice library unavailable</p>
                <p className="mt-1">{library.provider_error}</p>
              </div>
            ) : null}

            <section className="space-y-4 rounded-2xl border border-border/70 bg-white/45 p-4 sm:p-5" aria-labelledby="wise-friend-voices-heading">
              <div>
                <h3 id="wise-friend-voices-heading" className="font-semibold text-foreground">
                  WiseFriend voices
                </h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  Each voice keeps its ElevenLabs name. You can rename it later; the internal reference stays stable.
                </p>
              </div>

              <div className="grid gap-3 rounded-xl border border-dashed border-border bg-background/70 p-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
                <div className="space-y-1.5">
                  <Label htmlFor="new-elevenlabs-voice">ElevenLabs voice</Label>
                  <Select value={newVoiceId} onValueChange={setNewVoiceId}>
                    <SelectTrigger id="new-elevenlabs-voice">
                      <SelectValue placeholder="Choose a saved voice" />
                    </SelectTrigger>
                    <SelectContent>{voiceOptions()}</SelectContent>
                  </Select>
                </div>
                <Button
                  type="button"
                  size="auto"
                  disabled={
                    !newVoiceId
                    || actionInFlight !== null
                    || !library.provider_connected
                  }
                  onClick={() => { void addVoice(newVoiceId); }}
                >
                  <Plus aria-hidden="true" /> Add to WiseFriend
                </Button>
              </div>

              {library.mappings.length ? (
                <div className="grid gap-3 lg:grid-cols-2">
                  {library.mappings.map((mapping) => {
                    const isEditing = editingAlias === mapping.alias;
                    return (
                      <article
                        key={mapping.alias}
                        className="rounded-xl border border-border/70 bg-white p-4"
                        data-testid={`elevenlabs-mapping-${mapping.alias}`}
                      >
                        {isEditing ? (
                          <div className="space-y-3">
                            <div className="space-y-1.5">
                              <Label htmlFor={`mapping-name-${mapping.alias}`}>WiseFriend name</Label>
                              <Input
                                id={`mapping-name-${mapping.alias}`}
                                value={editName}
                                maxLength={80}
                                onChange={(event) => setEditName(event.target.value)}
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label htmlFor={`mapping-voice-${mapping.alias}`}>ElevenLabs voice</Label>
                              <Select
                                value={editVoiceId}
                                onValueChange={setEditVoiceId}
                                disabled={mapping.usage.active_session_count > 0}
                              >
                                <SelectTrigger id={`mapping-voice-${mapping.alias}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>{voiceOptions(mapping.voice, mapping.alias)}</SelectContent>
                              </Select>
                            </div>
                            {mapping.usage.active_session_count ? (
                              <p className="text-xs text-amber-800">
                                The ElevenLabs voice cannot change while an attempt is open. You can still rename it.
                              </p>
                            ) : null}
                            <div className="flex flex-wrap gap-2">
                              <Button
                                type="button"
                                size="auto"
                                disabled={!editName.trim() || !editVoiceId || actionInFlight !== null}
                                onClick={() => { void saveEdit(mapping); }}
                              >
                                Save mapping
                              </Button>
                              <Button
                                type="button"
                                size="auto"
                                variant="secondary"
                                disabled={actionInFlight !== null}
                                onClick={() => setEditingAlias(null)}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <div>
                              <h4 className="text-lg font-semibold text-foreground">{mapping.name}</h4>
                              <p className="text-sm text-muted-foreground">
                                ElevenLabs · {mapping.voice.name}
                              </p>
                            </div>
                            {!mapping.voice.available ? (
                              <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
                                This voice ID is configured but was not returned by the saved voice library.
                              </p>
                            ) : null}
                            <p className="text-xs font-medium text-muted-foreground">
                              {usageSummary(mapping)}
                            </p>
                            {mapping.usage.questions.length ? (
                              <ul className="space-y-1 text-xs text-muted-foreground">
                                {mapping.usage.questions.slice(0, 3).map((question) => (
                                  <li key={question.id}>• {question.title || question.key}</li>
                                ))}
                              </ul>
                            ) : null}
                            <div className="flex flex-wrap gap-2">
                              <Button
                                type="button"
                                size="auto"
                                variant="secondary"
                                disabled={actionInFlight !== null}
                                onClick={() => beginEdit(mapping)}
                              >
                                <Pencil aria-hidden="true" /> Edit mapping
                              </Button>
                              <Button
                                type="button"
                                size="auto"
                                variant="ghost"
                                disabled={actionInFlight !== null}
                                title="Remove this name from WiseFriend without changing ElevenLabs"
                                onClick={() => requestRemoval(mapping)}
                              >
                                Remove from WiseFriend
                              </Button>
                            </div>
                          </div>
                        )}
                      </article>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-border px-4 py-7 text-center text-sm text-muted-foreground">
                  No ElevenLabs voices have been added to WiseFriend yet.
                </div>
              )}
            </section>

          </>
        ) : (
          <div className="rounded-2xl border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
            ElevenLabs voice settings are unavailable.
          </div>
        )}
      </CardContent>

      {isVoiceLibraryOpen && library ? (
        <AppDialog
          onClose={() => setIsVoiceLibraryOpen(false)}
          title="ElevenLabs voice library"
          description="Preview voices already saved to your account or discover another voice from ElevenLabs. Provider voices are managed in ElevenLabs; WiseFriend only references them."
          size="xl"
          bodyClassName="min-h-0"
          actions={(
            <Button
              type="button"
              size="auto"
              variant="secondary"
              onClick={() => setIsVoiceLibraryOpen(false)}
            >
              Done
            </Button>
          )}
        >
          <Tabs
            value={voiceLibraryTab}
            onValueChange={(value) => setVoiceLibraryTab(value as VoiceLibraryTab)}
          >
            <TabsList className="grid h-auto w-full grid-cols-2 rounded-xl bg-muted/60 p-1">
              <TabsTrigger
                value="saved"
                className="gap-2 rounded-lg border-0 px-3 py-2.5 data-[state=active]:border-transparent data-[state=active]:bg-white data-[state=active]:shadow-sm"
              >
                <Library className="h-4 w-4" aria-hidden="true" />
                Saved voices ({library.voices.length})
              </TabsTrigger>
              <TabsTrigger
                value="explore"
                className="gap-2 rounded-lg border-0 px-3 py-2.5 data-[state=active]:border-transparent data-[state=active]:bg-white data-[state=active]:shadow-sm"
              >
                <Compass className="h-4 w-4" aria-hidden="true" />
                Explore voices
              </TabsTrigger>
            </TabsList>

            <TabsContent value="saved" className="mt-4 max-h-[60vh] space-y-4 overflow-y-auto pr-1">
              <label className="relative block w-full sm:max-w-md">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search name, accent, or ID"
                  aria-label="Search ElevenLabs voices"
                  className="pl-9"
                />
              </label>

              {filteredVoices.length ? (
                <div className="grid min-w-0 gap-3 md:grid-cols-2">
                  {filteredVoices.map((voice) => {
                    const mappedNames = library.mappings
                      .filter((mapping) => (voice.mapped_aliases ?? []).includes(mapping.alias))
                      .map((mapping) => mapping.name);
                    return (
                      <article
                        key={voice.voice_id}
                        className="flex min-w-0 flex-col gap-3 overflow-hidden rounded-xl border border-border/70 bg-white p-4"
                        data-testid={`elevenlabs-voice-${voice.voice_id}`}
                      >
                        <div className="flex flex-col items-start gap-2 sm:flex-row sm:justify-between sm:gap-3">
                          <div className="min-w-0">
                            <h4 className="break-words font-semibold text-foreground">{voice.name}</h4>
                            <p className="mt-0.5 break-all font-mono text-[10px] text-muted-foreground">{voice.voice_id}</p>
                          </div>
                          {voice.category ? <Badge variant="outline">{voice.category}</Badge> : null}
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {Object.entries(voice.labels).slice(0, 4).map(([key, value]) => (
                            <span key={key} className="rounded-full bg-muted px-2 py-1 text-[11px] text-muted-foreground">
                              {value}
                            </span>
                          ))}
                          {!voice.is_model_compatible ? (
                            <Badge variant="destructive">Incompatible model</Badge>
                          ) : null}
                        </div>
                        {voice.description ? (
                          <p className="line-clamp-2 text-xs leading-5 text-muted-foreground">{voice.description}</p>
                        ) : null}
                        {voice.preview_url ? (
                          <audio
                            controls
                            preload="none"
                            src={voice.preview_url}
                            aria-label={`Preview ${voice.name}`}
                            className="h-9 w-full min-w-0 max-w-full"
                          />
                        ) : (
                          <p className="text-xs text-muted-foreground">No preview is available.</p>
                        )}
                        <div className="mt-auto flex flex-wrap items-center justify-between gap-2 pt-1">
                          {mappedNames.length ? (
                            <Badge variant="secondary">In WiseFriend as {mappedNames.join(', ')}</Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">Not in WiseFriend yet</span>
                          )}
                          <Button
                            type="button"
                            size="auto"
                            variant="secondary"
                            disabled={
                              mappedNames.length > 0
                              || !voice.is_model_compatible
                              || actionInFlight !== null
                            }
                            onClick={() => { void addVoice(voice.voice_id); }}
                          >
                            <Plus aria-hidden="true" />
                            {actionInFlight === `create:${voice.voice_id}`
                              ? 'Adding...'
                              : mappedNames.length
                                ? 'Added'
                                : 'Add to WiseFriend'}
                          </Button>
                        </div>
                      </article>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
                  {library.provider_connected
                    ? 'No saved voices match this search.'
                    : 'Connect ElevenLabs and refresh to browse saved voices.'}
                </div>
              )}
            </TabsContent>

            <TabsContent value="explore" className="mt-4 max-h-[60vh] space-y-4 overflow-y-auto pr-1">
              <div>
                <h3 className="font-semibold text-foreground">Explore ElevenLabs voices</h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  Search the public Voice Library, listen to previews, and save a voice to your ElevenLabs account. Saved voices can then be added to WiseFriend from the Saved tab.
                </p>
              </div>
              <form
                className="flex flex-col gap-2 sm:flex-row"
                onSubmit={(event) => {
                  event.preventDefault();
                  void searchSharedVoices(0);
                }}
              >
                <label className="relative block min-w-0 flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                  <Input
                    value={sharedSearch}
                    onChange={(event) => setSharedSearch(event.target.value)}
                    placeholder="Search by name, style, accent, or voice ID"
                    aria-label="Search public ElevenLabs voice library"
                    className="pl-9"
                    maxLength={120}
                    disabled={!library.provider_connected || isSearchingShared}
                  />
                </label>
                <Button
                  type="submit"
                  size="auto"
                  variant="secondary"
                  disabled={!library.provider_connected || isSearchingShared || actionInFlight !== null}
                >
                  <Search className={isSearchingShared ? 'animate-pulse' : ''} aria-hidden="true" />
                  {isSearchingShared ? 'Searching...' : sharedResults ? 'Search again' : 'Browse voices'}
                </Button>
              </form>

              {sharedResults ? (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                    <span>
                      {sharedResults.total_count !== null
                        ? `${sharedResults.total_count.toLocaleString()} matching voices`
                        : `${sharedResults.voices.length} voices on this page`}
                    </span>
                    <span>Page {sharedResults.page + 1}</span>
                  </div>
                  {sharedResults.voices.length ? (
                    <div className="grid min-w-0 gap-3 md:grid-cols-2">
                      {sharedResults.voices.map((voice) => {
                        const wasSaved = recentlySavedSharedVoiceIds.has(voice.voice_id);
                        return (
                          <article
                            key={`${voice.public_owner_id}:${voice.voice_id}`}
                            className="flex min-w-0 flex-col gap-3 overflow-hidden rounded-xl border border-border/70 bg-white p-4"
                            data-testid={`elevenlabs-shared-voice-${voice.voice_id}`}
                          >
                            <div className="flex flex-wrap items-start justify-between gap-2">
                              <div className="min-w-0">
                                <h4 className="break-words font-semibold text-foreground">{voice.name}</h4>
                                {voice.category ? (
                                  <p className="mt-0.5 text-xs capitalize text-muted-foreground">{voice.category}</p>
                                ) : null}
                              </div>
                              {voice.rate !== null && voice.rate !== 1 ? (
                                <Badge variant="outline">Provider rate {voice.rate}×</Badge>
                              ) : null}
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {sharedVoiceLabels(voice).slice(0, 6).map((label) => (
                                <span key={label} className="rounded-full bg-muted px-2 py-1 text-[11px] capitalize text-muted-foreground">
                                  {label}
                                </span>
                              ))}
                            </div>
                            {voice.description ? (
                              <p className="line-clamp-3 text-xs leading-5 text-muted-foreground">{voice.description}</p>
                            ) : null}
                            {voice.preview_url ? (
                              <audio
                                controls
                                preload="none"
                                src={voice.preview_url}
                                aria-label={`Preview shared voice ${voice.name}`}
                                className="h-9 w-full min-w-0 max-w-full"
                              />
                            ) : (
                              <p className="text-xs text-muted-foreground">No preview is available.</p>
                            )}
                            <div className="mt-auto flex justify-end pt-1">
                              <Button
                                type="button"
                                size="auto"
                                variant="secondary"
                                disabled={wasSaved || actionInFlight !== null}
                                onClick={() => beginSaveSharedVoice(voice)}
                              >
                                <Plus aria-hidden="true" />
                                {wasSaved ? 'Saved to ElevenLabs' : 'Save to ElevenLabs'}
                              </Button>
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="rounded-xl border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
                      No shared voices match this search.
                    </div>
                  )}
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      type="button"
                      size="auto"
                      variant="ghost"
                      disabled={sharedResults.page === 0 || isSearchingShared || actionInFlight !== null}
                      onClick={() => { void searchSharedVoices(sharedResults.page - 1); }}
                    >
                      <ChevronLeft aria-hidden="true" /> Previous
                    </Button>
                    <Button
                      type="button"
                      size="auto"
                      variant="ghost"
                      disabled={!sharedResults.has_more || isSearchingShared || actionInFlight !== null}
                      onClick={() => { void searchSharedVoices(sharedResults.page + 1); }}
                    >
                      Next <ChevronRight aria-hidden="true" />
                    </Button>
                  </div>
                </>
              ) : (
                <div className="rounded-xl border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
                  {library.provider_connected
                    ? 'Browse trending voices or search for a specific style.'
                    : 'Connect ElevenLabs before exploring its public Voice Library.'}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </AppDialog>
      ) : null}

      {removalConflict ? (
        <AppDialog
          onClose={() => setRemovalConflict(null)}
          title="This voice is still in use"
          description={`“${removalConflict.name}” is still used by ${usageSummary(removalConflict).toLocaleLowerCase()}. Update the items below before trying again.`}
          size="md"
          bodyClassName="max-h-[60vh] space-y-5 overflow-y-auto pr-1"
          actions={(
            <Button
              type="button"
              size="auto"
              variant="secondary"
              onClick={() => setRemovalConflict(null)}
            >
              Close
            </Button>
          )}
        >
          {removalConflict.usage.questions.length ? (
            <section aria-labelledby="voice-removal-questions-heading">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 id="voice-removal-questions-heading" className="text-sm font-semibold text-foreground">
                    Questions to update
                  </h3>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Use the pencil to open the question in Question Library.
                  </p>
                </div>
                <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-900">
                  {removalConflict.usage.question_count}
                </Badge>
              </div>
              <ul className="mt-3 divide-y divide-border overflow-hidden rounded-xl border border-border bg-background">
                {removalConflict.usage.questions.map((question) => (
                  <li
                    key={question.id}
                    className="flex items-center justify-between gap-3 px-4 py-3"
                  >
                    <div className="min-w-0">
                      <p className="break-words text-sm font-medium text-foreground">
                        {question.title || question.key}
                      </p>
                      {!question.is_active ? (
                        <Badge variant="outline" className="mt-1.5">Inactive</Badge>
                      ) : null}
                    </div>
                    <Button asChild size="icon" variant="ghost">
                      <a
                        href={questionEditHref(question.id)}
                        aria-label={`Edit question ${question.title || question.key}`}
                        title="Edit question"
                      >
                        <Pencil aria-hidden="true" />
                      </a>
                    </Button>
                  </li>
                ))}
              </ul>
              {removalConflict.usage.question_count > removalConflict.usage.questions.length ? (
                <p className="mt-2 pl-1 text-xs text-muted-foreground">
                  {removalConflict.usage.question_count - removalConflict.usage.questions.length} additional question{removalConflict.usage.question_count - removalConflict.usage.questions.length === 1 ? '' : 's'} not shown.
                </p>
              ) : null}
            </section>
          ) : null}
          {removalConflict.usage.active_session_count ? (
            <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50/70 p-4 text-amber-950">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100">
                <TriangleAlert className="h-4 w-4" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold">
                  {removalConflict.usage.active_session_count} roleplay attempt{removalConflict.usage.active_session_count === 1 ? ' is' : 's are'} still open
                </p>
                <p className="mt-1 text-xs">Wait for the attempt to end before removing this voice name.</p>
              </div>
            </div>
          ) : null}
        </AppDialog>
      ) : null}

      {pendingRemoval ? (
        <AppDialog
          onClose={() => setPendingRemoval(null)}
          title={`Remove ${pendingRemoval.name} from WiseFriend?`}
          description="This removes the voice from WiseFriend only. The provider voice remains in ElevenLabs. WiseFriend rechecks question and open-attempt usage before removing the mapping."
          size="sm"
          isBusy={actionInFlight !== null}
          actions={(
            <>
              <Button
                type="button"
                size="auto"
                variant="secondary"
                onClick={() => setPendingRemoval(null)}
                disabled={actionInFlight !== null}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="auto"
                onClick={() => { void confirmRemoval(); }}
                disabled={actionInFlight !== null}
              >
                {actionInFlight ? 'Checking usage...' : 'Remove mapping'}
              </Button>
            </>
          )}
        />
      ) : null}

      {pendingSharedVoice ? (
        <AppDialog
          onClose={() => {
            setPendingSharedVoice(null);
            setSharedSavedName('');
          }}
          title={`Save ${pendingSharedVoice.name} to ElevenLabs?`}
          description="This adds the shared voice to the connected ElevenLabs account. You can then add it to WiseFriend from the Saved tab."
          size="sm"
          isBusy={actionInFlight !== null}
          actions={(
            <>
              <Button
                type="button"
                size="auto"
                variant="secondary"
                onClick={() => {
                  setPendingSharedVoice(null);
                  setSharedSavedName('');
                }}
                disabled={actionInFlight !== null}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="auto"
                onClick={() => { void confirmSaveSharedVoice(); }}
                disabled={!sharedSavedName.trim() || actionInFlight !== null}
              >
                {actionInFlight ? 'Saving...' : 'Save to ElevenLabs'}
              </Button>
            </>
          )}
        >
          <div className="space-y-1.5">
            <Label htmlFor="shared-elevenlabs-saved-name">Name in ElevenLabs</Label>
            <Input
              id="shared-elevenlabs-saved-name"
              value={sharedSavedName}
              maxLength={100}
              onChange={(event) => setSharedSavedName(event.target.value)}
              disabled={actionInFlight !== null}
            />
          </div>
        </AppDialog>
      ) : null}
    </Card>
  );
};

export default ElevenLabsRoleplayVoicesCard;
